import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Affiliate — customer referral program module (`/affiliates` on the v4 backend).
 * Covers affiliate CRUD, per-affiliate stats/clicks/conversions/commissions/payouts,
 * pending-invoice tracking, and payout management. Field names and endpoints are
 * lifted verbatim from the legacy `datacentral-client-v4` module so requests hit
 * unchanged. Some list responses return capital-R `Results` — normalised to
 * lowercase `results` before returning (same pattern as legacy).
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

export type AffiliateTier = 'standard' | 'silver' | 'gold' | 'platinum';
export type AffiliateStatus = 'pending' | 'active' | 'suspended' | 'inactive';

/** Mirrors the backend `payout_status` enum exactly (`cancelled` = denied, not `rejected`). */
export type PayoutStatus =
  | 'pending'
  | 'approved'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type CommissionStatus = 'pending' | 'confirmed' | 'refunded';
export type PolicyMatchReason =
  | 'product'
  | 'product_group'
  | 'affiliate_default'
  | 'global_default'
  | 'affiliate_fallback';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface Affiliate {
  affiliate_id: number;
  referrer_id: number;
  customer_id?: number;
  referral_code: string;
  referral_link: string;
  tier: AffiliateTier;
  commission_percent: number;
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  payout_threshold: number;
  status: AffiliateStatus;

  /** Server-computed aggregates — do not add client-side counts. */
  total_clicks: number;
  total_signups: number;
  total_conversions: number;
  total_revenue: number;
  total_commission: number;
  unpaid_commission: number;

  referrer_name?: string;
  referrer_email?: string;
  referrer_phone?: string;
  referrer_company?: string;

  created_at: string;
  updated_at: string;
}

export interface AffiliateStats {
  total_clicks: number;
  unique_visitors: number;
  total_signups: number;
  total_conversions: number;
  conversion_rate: number;
  total_revenue: number;
  total_commission: number;
  pending_commission: number;
  paid_commission: number;
}

export interface AffiliateClick {
  click_id: number;
  referrer_id: number;
  promo_code?: string;
  visitor_id?: string;
  ip_address?: string;
  user_agent?: string;
  referer_url?: string;
  landing_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  converted: boolean;
  converted_customer_id?: number;
  converted_at?: string;
  clicked_at: string;
}

export interface AffiliateConversion {
  click_id: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  converted_at: string;
  customer_created_at: string;
  total_revenue: number;
  total_commission: number;
  commission_count: number;
  commission_status?: string;
}

export interface AffiliateCommission {
  commission_id: number;
  referrer_id: number;
  customer_id?: number;
  invoice_id?: number;
  click_id?: number;
  revenue_amount: number;
  commission_percent: number;
  commission_amount: number;
  status: CommissionStatus;
  created_at: string;
  confirmed_at?: string;
  refunded_at?: string;
  policy_id?: number;
  base_price?: number;
  quarterly_discount?: number;
  marketing_discount?: number;
  amount_before_vat?: number;
  gross_commission?: number;
  tax_rate?: number;
  tax_amount?: number;
  net_commission?: number;
  match_reason?: PolicyMatchReason;
  auto_confirm_at?: string;
  service_id?: number;
  product_id?: number;
  product_group_id?: number;
  promo_ids?: number[];
}

export interface AffiliateCommissionSummary {
  total_count: number;
  total_revenue: number;
  total_commission: number;
  confirmed_commission: number;
  pending_commission: number;
  refunded_commission: number;
}

export interface AffiliatePayout {
  payout_id: number;
  referrer_id: number;
  referrer_name?: string;
  referral_code?: string;
  amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  status: PayoutStatus;
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  transaction_ref?: string;
  notes?: string;
  rejection_reason?: string;
  requested_at: string;
  approved_at?: string;
  processed_at?: string;
  created_at: string;
  updated_at: string;
}

/** One commission line item inside a payout settlement batch. */
export interface PayoutCommission {
  commission_id: number;
  customer_id?: number;
  customer_name?: string;
  customer_email?: string;
  invoice_id?: number;
  revenue_amount: number;
  commission_percent: number;
  commission_amount: number;
  status: string;
  invoice_payment_at?: string;
  effective_payment_at?: string;
  paid_at?: string;
  reversed_after_payout_at?: string;
}

export interface AffiliatePendingInvoice {
  invoice_id: number;
  customer_id: number;
  customer_name: string;
  invoice_amount: number;
  invoice_total: number;
  invoice_status: string;
  invoice_created_at: string;
  service_id?: number;
  product_id?: number;
  product_group_id?: number;
  commission_percent: number;
  expected_net_commission: number;
}

/** Contact-role candidate row — fields used by the Candidates table. */
export interface AffiliateCandidate {
  customer_contact_id: number;
  customer_id: number;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_position: string;
  contact_roles: string[];
  customer_name: string;
  customer_email: string;
  linked_entity_type?: string;
  linked_entity_id?: number;
  linked_entity_name?: string;
  linked_entity_status?: string;
  created_at: string;
  updated_at: string;
}

/* ── Params ────────────────────────────────────────────────────────────── */

/** Query params for `GET /affiliates`. Uses limit/offset (not page/page_size). */
export interface AffiliateListParams {
  search?: string;
  limit?: number;
  offset?: number;
  referrer_type?: 'employee' | 'non_employee';
  status?: string;
  tier?: string;
}

/** Mutation payloads */
export interface CreateAffiliatePayload {
  referrer_name: string;
  referrer_email: string;
  referrer_phone?: string;
  referrer_company?: string;
  customer_id?: number;
  referral_code?: string;
  tier?: AffiliateTier;
  commission_percent?: number;
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  payout_threshold?: number;
}

export interface UpdateAffiliatePayload {
  tier?: AffiliateTier;
  commission_percent?: number;
  payout_method?: string;
  payout_details?: Record<string, unknown>;
  payout_threshold?: number;
  status?: AffiliateStatus;
}

export interface CreatePayoutPayload {
  amount: number;
  currency?: string;
  period_start: string;
  period_end: string;
  notes?: string;
}

export interface UpdatePayoutPayload {
  status?: PayoutStatus;
  transaction_ref?: string;
  notes?: string;
  rejection_reason?: string;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

/** Normalise capital-R `Results` that some endpoints return instead of `results`. */
function normalizeResults<T>(data: ResponseArray<T> & { Results?: T[] }): ResponseArray<T> {
  if (data?.Results && !data?.results) {
    (data as ResponseArray<T>).results = data.Results;
  }
  return data;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const affiliateKeys = {
  all: ['affiliates'] as const,
  list: (params: AffiliateListParams) => ['affiliates', 'list', params] as const,
  detail: (id: number) => ['affiliates', 'detail', id] as const,
  stats: (id: number) => ['affiliates', id, 'stats'] as const,
  clicks: (id: number, params?: object) => ['affiliates', id, 'clicks', params] as const,
  conversions: (id: number, params?: object) => ['affiliates', id, 'conversions', params] as const,
  commissions: (id: number, params?: object) => ['affiliates', id, 'commissions', params] as const,
  commissionsSummary: (id: number) => ['affiliates', id, 'commissions', 'summary'] as const,
  pendingInvoices: (id: number, params?: object) => ['affiliates', id, 'pending-invoices', params] as const,
  payouts: (id: number, params?: object) => ['affiliates', id, 'payouts', params] as const,
  payoutCommissions: (payoutId: number) => ['payouts', payoutId, 'commissions'] as const,
  candidates: (params?: object) => ['affiliates', 'candidates', params] as const,
  employeeAffiliate: (employeeId: number) => ['employee-affiliate', employeeId] as const,
};

/* ── Affiliate list ─────────────────────────────────────────────────────── */

/** `GET /affiliates` — paginated affiliate list (limit/offset, not page/page_size). */
export function useAffiliates(params: AffiliateListParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.list(params),
    queryFn: async (): Promise<ResponseArray<Affiliate>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      };
      if (params.search) p.search = params.search;
      if (params.referrer_type) p.referrer_type = params.referrer_type;
      if (params.status) p.status = params.status;
      if (params.tier) p.tier = params.tier;
      const res = await dataCentralApi.get<ResponseArray<Affiliate> & { Results?: Affiliate[] }>(
        `/affiliates?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Affiliate detail ───────────────────────────────────────────────────── */

/** `GET /affiliates/:id` — full affiliate record with server-computed live stats. */
export function useAffiliate(id: number) {
  return useQuery({
    queryKey: affiliateKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<Affiliate>> => {
      const res = await dataCentralApi.get<ResponseObject<Affiliate>>(`/affiliates/${id}`);
      return res.data;
    },
  });
}

/* ── Stats ──────────────────────────────────────────────────────────────── */

/** `GET /affiliates/:id/stats` — aggregate stat totals for one affiliate. */
export function useAffiliateStats(id: number) {
  return useQuery({
    queryKey: affiliateKeys.stats(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<AffiliateStats>> => {
      const res = await dataCentralApi.get<ResponseObject<AffiliateStats>>(
        `/affiliates/${id}/stats`,
      );
      return res.data;
    },
  });
}

/* ── Clicks ─────────────────────────────────────────────────────────────── */

export interface AffiliateClicksParams {
  limit?: number;
  offset?: number;
  /** Filter by conversion outcome (`true` = converted, `false` = not converted). */
  converted?: boolean;
}

/** `GET /affiliates/:id/clicks` — paginated click log. */
export function useAffiliateClicks(id: number, params: AffiliateClicksParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.clicks(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<AffiliateClick>> => {
      const p: Record<string, unknown> = { limit: params.limit ?? 10, offset: params.offset ?? 0 };
      if (params.converted !== undefined) p.converted = params.converted;
      const res = await dataCentralApi.get<ResponseArray<AffiliateClick> & { Results?: AffiliateClick[] }>(
        `/affiliates/${id}/clicks?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Conversions ────────────────────────────────────────────────────────── */

export interface AffiliateConversionsParams {
  limit?: number;
  offset?: number;
}

/** `GET /affiliates/:id/conversions` — paginated conversion list. */
export function useAffiliateConversions(id: number, params: AffiliateConversionsParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.conversions(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<AffiliateConversion>> => {
      const p = { limit: params.limit ?? 10, offset: params.offset ?? 0 };
      const res = await dataCentralApi.get<ResponseArray<AffiliateConversion> & { Results?: AffiliateConversion[] }>(
        `/affiliates/${id}/conversions?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Commissions ────────────────────────────────────────────────────────── */

export interface AffiliateCommissionsParams {
  limit?: number;
  offset?: number;
  status?: CommissionStatus;
}

/** `GET /affiliates/:id/commissions` — paginated commission audit trail. */
export function useAffiliateCommissions(id: number, params: AffiliateCommissionsParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.commissions(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<AffiliateCommission>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      };
      if (params.status) p.status = params.status;
      const res = await dataCentralApi.get<ResponseArray<AffiliateCommission> & { Results?: AffiliateCommission[] }>(
        `/affiliates/${id}/commissions?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/** `GET /affiliates/:id/commissions/summary` — aggregate commission totals. */
export function useAffiliateCommissionsSummary(id: number) {
  return useQuery({
    queryKey: affiliateKeys.commissionsSummary(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<AffiliateCommissionSummary>> => {
      const res = await dataCentralApi.get<ResponseObject<AffiliateCommissionSummary>>(
        `/affiliates/${id}/commissions/summary`,
      );
      return res.data;
    },
  });
}

/* ── Pending invoices ───────────────────────────────────────────────────── */

export interface AffiliatePendingInvoicesParams {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
}

/** `GET /affiliates/:id/pending-invoices` — invoices awaiting commission confirmation. */
export function useAffiliatePendingInvoices(id: number, params: AffiliatePendingInvoicesParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.pendingInvoices(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<AffiliatePendingInvoice>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      };
      if (params.status) p.status = params.status;
      if (params.search) p.search = params.search;
      const res = await dataCentralApi.get<ResponseArray<AffiliatePendingInvoice> & { Results?: AffiliatePendingInvoice[] }>(
        `/affiliates/${id}/pending-invoices?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Payouts ────────────────────────────────────────────────────────────── */

export interface AffiliatePayoutsParams {
  limit?: number;
  offset?: number;
  status?: PayoutStatus;
}

/** `GET /affiliates/:id/payouts` — paginated payout list. */
export function useAffiliatePayouts(id: number, params: AffiliatePayoutsParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.payouts(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<AffiliatePayout>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      };
      if (params.status) p.status = params.status;
      const res = await dataCentralApi.get<ResponseArray<AffiliatePayout> & { Results?: AffiliatePayout[] }>(
        `/affiliates/${id}/payouts?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/** `GET /payouts/:id/commissions` — commission line items for a payout settlement. */
export function usePayoutCommissions(payoutId: number) {
  return useQuery({
    queryKey: affiliateKeys.payoutCommissions(payoutId),
    enabled: !!payoutId,
    queryFn: async (): Promise<PayoutCommission[]> => {
      const res = await dataCentralApi.get<{ result?: PayoutCommission[]; results?: PayoutCommission[] }>(
        `/payouts/${payoutId}/commissions`,
      );
      return res.data?.result ?? res.data?.results ?? [];
    },
  });
}

/* ── Candidates ─────────────────────────────────────────────────────────── */

export interface AffiliateCandidatesParams {
  q?: string;
  page?: number;
  page_size?: number;
}

/** `GET /customers/contacts/by-role?role=affiliate` — contacts eligible to become affiliates. */
export function useAffiliateCandidates(params: AffiliateCandidatesParams = {}) {
  return useQuery({
    queryKey: affiliateKeys.candidates(params),
    queryFn: async (): Promise<ResponseArray<AffiliateCandidate>> => {
      const p: Record<string, unknown> = { role: 'affiliate', page: params.page ?? 1, page_size: params.page_size ?? 10 };
      if (params.q) p.q = params.q;
      const res = await dataCentralApi.get<ResponseArray<AffiliateCandidate> & { Results?: AffiliateCandidate[] }>(
        `/customers/contacts/by-role?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Employee affiliate ─────────────────────────────────────────────────── */

/**
 * `GET /employee-affiliate/:employeeId` — resolve (provision if missing) the
 * affiliate record for an employee so the employee-affiliate list can open the
 * standard affiliate detail with all its tabs.
 */
export function useEmployeeAffiliate(employeeId: number) {
  return useQuery({
    queryKey: affiliateKeys.employeeAffiliate(employeeId),
    enabled: !!employeeId,
    queryFn: async (): Promise<ResponseObject<Affiliate>> => {
      const res = await dataCentralApi.get<ResponseObject<Affiliate>>(
        `/employee-affiliate/${employeeId}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ──────────────────────────────────────────────────────────── */

/** `POST /affiliates` — create a new affiliate. */
export function useCreateAffiliate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAffiliatePayload): Promise<ResponseObject<Affiliate>> => {
      const res = await dataCentralApi.post<ResponseObject<Affiliate>>('/affiliates', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.all });
    },
  });
}

/** `PUT /affiliates/:id` — update an existing affiliate. */
export function useUpdateAffiliate(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateAffiliatePayload): Promise<ResponseObject<Affiliate>> => {
      const res = await dataCentralApi.put<ResponseObject<Affiliate>>(`/affiliates/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.all });
    },
  });
}

/** `DELETE /affiliates/:id` — soft-delete an affiliate. */
export function useDeleteAffiliate(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResponseObject<Affiliate>> => {
      const res = await dataCentralApi.delete<ResponseObject<Affiliate>>(`/affiliates/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.all });
    },
  });
}

/** `POST /affiliates/:id/payouts` — create a payout request for an affiliate. */
export function useCreateAffiliatePayout(affiliateId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePayoutPayload): Promise<ResponseObject<AffiliatePayout>> => {
      const res = await dataCentralApi.post<ResponseObject<AffiliatePayout>>(
        `/affiliates/${affiliateId}/payouts`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.payouts(affiliateId) });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.all });
    },
  });
}

/** `PUT /payouts/:id` — update payout status/details (approve, process, cancel). */
export function useUpdatePayout(payoutId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdatePayoutPayload): Promise<ResponseObject<AffiliatePayout>> => {
      const res = await dataCentralApi.put<ResponseObject<AffiliatePayout>>(
        `/payouts/${payoutId}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.all });
    },
  });
}
